<template>
  <div class="grid grid-rows mx-auto mb-4" id="messages-root">
    <!-- Message List View -->
    <div v-if="!selectedMessage">
      <div v-if="messages.length === 0" class="grid grid-rows p-6 my-6 callout">
        {{ emptyMessage }}
      </div>

      <div v-else class="rounded-lg">
        <div
          v-for="(message, index) in messages"
          :key="message.id"
          class="flex border-b border-gray-200"
          :class="{ 'font-bold': !message.read }"
        >
          <div class="w-full cursor-pointer" @click.prevent="selectMessage(message)">
            <div class="flex flex-col md:flex-row">
              <div class="p-2 md:p-4 w-full md:w-7/12">
                {{ message.subject || 'Geen onderwerp' }}
              </div>

              <div class="p-2 md:p-4 w-full md:w-2/12">
                {{ message.patient_name || '-' }}
              </div>

              <div class="p-2 md:p-4 w-full md:w-2/12">
                {{ formatDate(message.received_at) }}
              </div>

              <div class="p-2 md:p-4 md:w-2/12 hidden md:block text-primary-default font-bold text-end">
                Bekijk bericht
              </div>
            </div>
          </div>

          <!-- OPTIONS -->
          <div class="p-2 md:p-4 w-4 relative content-center">
            <button @click.stop="toggleDropdown(index)" class="rounded-full p-1 text-gray-400 hover:text-gray-600">
              <EllipsisVerticalIcon class="h-5 w-5" />
            </button>

            <div
              v-if="openDropdownIndex === index"
              class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <a
                v-if="message.read"
                @click.stop.prevent="markAsUnread(message)"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                Markeer als ongelezen
              </a>

              <a
                v-else
                @click.stop.prevent="markAsRead(message)"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
              >
                Markeer als gelezen
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Detail View -->
    <div v-else class="p-4">
      <button @click="selectedMessage = null" class="flex items-center text-pdsPrimaryColor mb-4">
        <ArrowLeftIcon class="h-5 w-5 mr-2" />
        Terug naar alle berichten
      </button>

      <div class="space-y-4">
        <div class="text-xl font-bold">
          {{ selectedMessage.subject || 'Geen onderwerp' }}
        </div>

        <div>
          <span class="font-bold">Datum:</span>
          {{ formatDate(selectedMessage.received_at) }}
        </div>

        <div v-if="selectedMessage.patient_name">
          <span class="font-bold">Patiënt:</span>

          <span class="underline cursor-pointer ml-2" @click="handlePatientClick(selectedMessage.patient_id)">
            {{ selectedMessage.patient_name }}
          </span>
        </div>

        <div class="pt-2" v-html="selectedMessage.body || selectedMessage.content" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { EllipsisVerticalIcon, ArrowLeftIcon } from '@heroicons/vue/20/solid';
import { currentUserIdentity$, activeDepartment$ } from '@/services';
import router from '@/router';
import { useStore } from 'vuex';

export default {
  components: { EllipsisVerticalIcon, ArrowLeftIcon },

  props: {
    patientId: Number,
    mode: {
      type: String,
      default: 'dashboard'
    }
  },
  emits: ['message-count', 'switch-view', 'unread-count'],
  setup(props, { emit }) {
    const store = useStore();

    const messages = ref([]);
    const selectedMessage = ref(null);
    const user = ref(null);
    const activeDepartment = ref(null);
    const openDropdownIndex = ref(null);
    const baseUrl = window.location.origin;
    let userSubscription = null;
    let departmentSubscription = null;
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;

    const emptyMessage = computed(() => {
      return props.mode === 'patient'
        ? 'Er zijn geen berichten beschikbaar voor deze patiënt.'
        : 'Er zijn geen berichten beschikbaar.';
    });

    const formatDate = (date) => {
      if (!date) return 'Onbekend';
      return new Date(date).toLocaleDateString('nl-NL');
    };

    const fetchMessages = async () => {
      if (!user.value?.id || !activeDepartment.value) return;

      let url = '';

      if (props.mode === 'dashboard') {
        url = `${baseUrl}/api/sitecore/v2/Notification/GetPrescriberInboxMessages?userId=${user.value.id}&departmentId=${activeDepartment.value.ppwRecId}`;
      } else {
        url = `${baseUrl}/api/sitecore/v2/Notification/GetInboxMessages?patientId=${props.patientId}&userId=${user.value.id}&departmentId=${activeDepartment.value.ppwRecId}`;
      }

      const { data } = await axios.get(url);
      messages.value = data || [];
      emitUnreadCounts();
    };

    const emitUnreadCounts = () => {
      const unreadCount = messages.value.filter((m) => !m.read).length;
      emit('unread-count', unreadCount);
      $emitter.emit(
        'notification-messages',
        messages.value.filter((msg) => msg.read === false)
      );
    };

    const selectMessage = async (message) => {
      selectedMessage.value = message;

      if (!message.read) {
        message.read = true;

        const endpoint = props.mode === 'dashboard' ? 'MarkPrescriberMessageAsRead' : 'MarkMessageAsRead';

        await axios.post(`${baseUrl}/api/sitecore/v2/Notification/${endpoint}`, {
          messageId: message.id,
          inboxId: message.inbox_id || message.inboxId,
          patientId: props.patientId,
          userId: user.value.id,
          departmentId: activeDepartment.value?.ppwRecId
        });
        emitUnreadCounts();
      }
    };

    const markAsRead = (m) => {
      closeDropdown();
      selectMessage(m);
    };

    const markAsUnread = async (message) => {
      closeDropdown();
      message.read = false;

      const endpoint = props.mode === 'dashboard' ? 'MarkPrescriberMessageAsUnread' : 'MarkMessageAsUnread';

      await axios.post(`${baseUrl}/api/sitecore/v2/Notification/${endpoint}`, {
        messageId: message.id,
        inboxId: message.inbox_id || message.inboxId,
        patientId: props.patientId,
        userId: user.value.id,
        departmentId: activeDepartment.value?.ppwRecId
      });
      emitUnreadCounts();
    };

    const handlePatientClick = async (id) => {
      if (props.mode === 'dashboard') {
        await store.dispatch('patient/setActivePatient', id);
        router.push('/patientdashboard');
      } else {
        emit('switch-view', 'dashboard');
      }
    };

    const toggleDropdown = (i) => {
      openDropdownIndex.value = openDropdownIndex.value === i ? null : i;
    };

    const closeDropdown = () => {
      openDropdownIndex.value = null;
    };

    const handleClickOutside = (event) => {
      const el = document.getElementById('messages-root');
      if (el && !el.contains(event.target)) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      userSubscription = currentUserIdentity$.subscribe((currentUser) => {
        user.value = currentUser;
        fetchMessages();
      });

      departmentSubscription = activeDepartment$.subscribe((department) => {
        activeDepartment.value = department;
        fetchMessages();
      });

      $emitter.on('message-click', (message) => {
        selectMessage(message);
      });
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      if (userSubscription) {
        userSubscription.unsubscribe();
      }

      if (departmentSubscription) {
        departmentSubscription.unsubscribe();
      }
    });

    return {
      messages,
      selectedMessage,
      openDropdownIndex,
      toggleDropdown,
      markAsRead,
      markAsUnread,
      selectMessage,
      formatDate,
      handlePatientClick,
      emptyMessage,
      handleClickOutside,
      closeDropdown
    };
  }
};
</script>
